"use client";

import React from "react";
import type { ComponentType, SVGProps } from "react";
import type { PlaceholderIconProps } from "@/components/icons/icons-utils/icon-props";
import { RadixIconsCornerBottomLeft } from "@/components/icons/corner-bottom-left";
import { RadixIconsCornerBottomRight } from "@/components/icons/corner-bottom-right";
import { RadixIconsCornerTopLeft } from "@/components/icons/corner-top-left";
import { RadixIconsCornerTopRight } from "@/components/icons/corner-top-right";
import { RadixIconsCorners } from "@/components/icons/corners";
import { resolve_icon_layout_size_px } from "@/components/icons/icons-utils/icon-size-tokens";
import { resolve_icon_stroke_width } from "@/components/icons/icons-utils/icon-stroke-config";
import { MouseScrollIcon as MouseScrollIconInner } from "@/components/icons/mouse-scroll-icon";
import { ResetIcon } from "@/components/icons/reset";

// MARK: wrap_svg_layout_icon
/**
 * Adapts legacy Radix SVG components to {@link PlaceholderIconProps}.
 *
 * **Reuse scope:** Local — `custom-layout-icons` + registry only.
 */
function wrap_svg_layout_icon(
    display_name: string,
    Svg: ComponentType<SVGProps<SVGSVGElement>>,
): React.FC<PlaceholderIconProps> {
    const C = (props: PlaceholderIconProps) => {
        const {
            color = "neutral",
            colorIntensity = 500,
            size: size_prop,
            strokeWidth,
            style,
            className,
            width,
            height,
            ...rest
        } = props;
        const resolved_size = resolve_icon_layout_size_px(size_prop);
        const width_rem = `${resolved_size / 16}rem`;
        const height_rem = `${resolved_size / 16}rem`;
        const default_style: React.CSSProperties = {
            color: `var(--${String(color)}-${colorIntensity})`,
        };
        return (
            <Svg
                width={width ?? width_rem}
                height={height ?? height_rem}
                style={{ ...default_style, ...style }}
                className={className}
                strokeWidth={resolve_icon_stroke_width(resolved_size, strokeWidth)}
                {...rest}
            />
        );
    };
    C.displayName = display_name;
    return C;
}

// MARK: MouseScrollIcon
/** **Reuse scope:** Project-wide — `@/components/icons` registry. */
export const MouseScrollIcon: React.FC<PlaceholderIconProps> = (props) => {
    const {
        color = "neutral",
        colorIntensity = 500,
        size: size_prop,
        strokeWidth,
        style,
        className,
        width,
        height,
    } = props;
    const resolved_size = resolve_icon_layout_size_px(size_prop);
    const width_rem = `${resolved_size / 16}rem`;
    const height_rem = `${resolved_size / 16}rem`;
    const default_style: React.CSSProperties = {
        color: `var(--${String(color)}-${colorIntensity})`,
    };
    return (
        <MouseScrollIconInner
            width={width ?? width_rem}
            height={height ?? height_rem}
            className={className}
            style={{ ...default_style, ...style }}
            strokeWidth={resolve_icon_stroke_width(resolved_size, strokeWidth)}
        />
    );
};
MouseScrollIcon.displayName = "MouseScrollIcon";

export const CornerBottomLeft = wrap_svg_layout_icon(
    "CornerBottomLeft",
    RadixIconsCornerBottomLeft,
);
export const CornerBottomRight = wrap_svg_layout_icon(
    "CornerBottomRight",
    RadixIconsCornerBottomRight,
);
export const CornerTopLeft = wrap_svg_layout_icon(
    "CornerTopLeft",
    RadixIconsCornerTopLeft,
);
export const CornerTopRight = wrap_svg_layout_icon(
    "CornerTopRight",
    RadixIconsCornerTopRight,
);
export const Corners = wrap_svg_layout_icon("Corners", RadixIconsCorners);
export const Reset = wrap_svg_layout_icon("Reset", ResetIcon);

/** Keys merged into {@link ICON_COMPONENT_BY_NAME} (canonical PascalCase). */
export const CUSTOM_LAYOUT_ICON_COMPONENT_BY_NAME: Record<
    string,
    React.FC<PlaceholderIconProps>
> = {
    CornerBottomLeft,
    CornerBottomRight,
    CornerTopLeft,
    CornerTopRight,
    Corners,
    MouseScrollIcon,
    Reset,
};
